/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 3/4/2023
 */

import { Close } from "@mui/icons-material";
import { Button, IconButton, OutlinedInput, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ImageUploader from "../Shared/ImageUploader";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import CategoryList from "../Shared/CategoryList";
import {
  useGetCategoryByIdQuery,
  useUpdateCategoryMutation,
} from "../../Store/Services/CategoryService";
import AttributeTable from "./attributeTable";
import styles from "./AddCategory.module.css";
import { useDispatch, useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/dist/query";

const schema = yup.object().shape({
  catName: yup.string().required(),
  catDisplay: yup.string().required(),
  catDesc: yup.string().required(),
  attributes: yup.array().of(
    yup.object().shape({
      attributeIndex: yup.string().required(),
      attributeName: yup.string().required(),
      pickerType: yup.string().required(),
      mandatory: yup.bool().required(),
      attributeValues: yup.array().of(
        yup.object().shape({
          value: yup.string().required(),
        }).required()
      ).required(),
    }).required()
  ).required(),
});

const EditCategory = ({ handleClose }) => {
  const category = useSelector((state) => state.category);

  const [deletedAttributes, setDeletedAttributes] = useState([]);
  const [deletedValues, setDeletedValues] = useState([]);

  const [images, setImages] = useState([]);
  const [catName, setCatName] = useState("");
  const [openCatList, setOpenCatList] = useState(false);
  const [selectedCat, setSelectedCat] = useState("-1");

  let firstCategory = useMemo(() => {
    return { attributes: [] };
  }, []);

  // Form Validation
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      attributes: firstCategory.attributes,
    },
  });
  
  const {
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const {
    data: catDetails,
    isSuccess: catSuccess,
    isLoading: catLoading,
  } = useGetCategoryByIdQuery(
    category.selectedCategory ? category.selectedCategory : skipToken
  );

  const {
    data: parentCatDetails,
    isSuccess: parentCatSuccess,
    isLoading: parentCatLoading,
  } = useGetCategoryByIdQuery(catDetails?.parentCat || skipToken);

  useEffect(() => {
    if (
      parentCatSuccess &&
      Array.isArray(parentCatDetails) &&
      parentCatDetails.length > 0
    ) {
      const parentCategory = parentCatDetails[0];
      setCatName(parentCategory?.catDisplay);
    }
  }, [parentCatDetails, parentCatSuccess]);

  const setParentCat = () => {
    setSelectedCat(catDetails?.parentCat);
    // setCatName(catDetails?.parentCategory?.catDisplay);
    setCatName(catDetails.parentCatName); 
    setImages(catDetails?.imageList?.split(",") || []);
  };

  useEffect(() => {
    if (catSuccess && catDetails) {
        const attributesMap = {};

        catDetails?.catAttributeDTOS?.forEach(attr => {
            if (!attributesMap[attr.catAttributeId]) {
                attributesMap[attr.catAttributeId] = {
                    attributeIndex: attr.attributeIndex,
                    attributeName: attr.attributeName,
                    pickerType: attr.attributeTypeId || "-1",
                    mandatory: attr.isMandatory || false,
                    attributeValues: [],
                };
            }
            if (attr.catAttributeValueDTOS && attr.catAttributeValueDTOS.length > 0) {
                attributesMap[attr.catAttributeId].attributeValues.push({
                    value: attr.catAttributeValueDTOS[0]?.catAttributeValue || "",
                });
            }
        });

        const attributes = Object.values(attributesMap);

        methods.reset({
            catName: catDetails.catName,
            catDisplay: catDetails.catDisplay,
            catDesc: catDetails.catDesc,
            attributes: attributes,
        });

        setParentCat();
        setImages(catDetails.imageList?.split(",") || []);
    }
}, [catDetails, catSuccess]);


  const [updateCategory] = useUpdateCategoryMutation();

  const onSubmit = async (data) => {
    const dataSet = {
      catId: category.selectedCategory,
      ...data,
      parentCat: selectedCat,
      deleted: {
        attributes: deletedAttributes,
        values: deletedValues,
      },
      imageList: images?.toString(),
    };

    if (images.length !== 0) {
      try {
        await updateCategory(dataSet).unwrap();
        toast.success("Category successfully added!", {
          position: "bottom-right",
        });
      } catch (err) {
        console.error("Failed to save the category: ", err);
        toast.error("Error, something went wrong!", {
          position: "bottom-right",
        });
      }
      handleClose();
    }
  };

  return (
    <div>
      <div className={styles.headingContainer}>
        <Typography fontWeight={600} variant={"h6"}>
          Edit Category
        </Typography>
        <IconButton className={styles.closeButton} onClick={handleClose}>
          <Close />
        </IconButton>
      </div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className={styles.formItem}>
            <label className={styles.formLabel}>Name:</label>
            {errors.catName && errors.catName.type === "required" && (
              <span className={styles.validation}> *</span>
            )}
            <OutlinedInput
              {...methods.register("catName")}
              className={styles.formInput}
              placeholder="Enter Category Name"
              size="small"
              fullWidth
            />
          </div>

          <div className={styles.formItem}>
            <label className={styles.formLabel}>Display Name:</label>
            {errors.catDisplay && errors.catDisplay.type === "required" && (
              <span className={styles.validation}> *</span>
            )}
            <OutlinedInput
              {...methods.register("catDisplay")}
              className={styles.formInput}
              placeholder="Enter Display Name"
              size="small"
              fullWidth
            />
          </div>

          <div className={styles.formItem}>
            <label className={styles.formLabel}>Category Description:</label>
            {errors.catDesc && errors.catDesc.type === "required" && (
              <span className={styles.validation}> *</span>
            )}
            <OutlinedInput
              {...methods.register("catDesc")}
              className={styles.formInput}
              placeholder="Enter Description"
              size="small"
              fullWidth
              multiline
            />
          </div>

          <div className={styles.formItem}>
            <label className={styles.formLabel}>Parent Category:</label>
            <OutlinedInput
              className={styles.formInput}
              placeholder="Enter Parent Category"
              size="small"
              onClick={() => setOpenCatList(true)}
              autoComplete="off"
              fullWidth
              value={catName}
            />
          </div>
          <div className={styles.formItem}>
            <label className={styles.formLabel}>Images:</label>
            {images.length === 0 && (
              <span className={styles.validation}> *</span>
            )}
            <ImageUploader
              images={images}
              setImages={setImages}
              from="editCat"
              id={catDetails?.catId}
            />
          </div>

          <div style={{ marginTop: "1rem" }}>
            <label style={{ fontWeight: 600 }}>Attributes:</label>
            <AttributeTable
              from="edit"
              deletedAttributes={deletedAttributes}
              setDeletedAttributes={setDeletedAttributes}
              defaultAttributes={methods.watch("attributes")}
              deletedValues={deletedValues}
              setDeletedValues={setDeletedValues}
            />
          </div>

          <div className={styles.buttonContainer}>
            <Button
              type="submit"
              className={styles.submitButton}
              variant="contained"
            >
              Save
            </Button>
          </div>
        </form>
      </FormProvider>
      <CategoryList
        open={openCatList}
        onClose={() => setOpenCatList(false)}
        setSelectedCat={setSelectedCat}
        selectedCat={selectedCat}
        setCatName={setCatName}
      />
    </div>
  );
};

export default EditCategory;
